import React, { useEffect, useMemo } from 'react';
import styles from './RichTextEditor.module.scss';
import { Bold, Italic, Strikethrough, List, ListOrdered, Quote, Minus, Image as ImageIcon, Eraser} from 'lucide-react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
// import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TurndownService from 'turndown';
import { marked } from 'marked';

// --- Utils encodage ---
const isLikelyHTML = (s='') => /^\s*</.test(s.trim());
const toHTML = (value) => isLikelyHTML(value) ? value : marked.parse(value || '');
const toMarkdown = (() => {
  const td = new TurndownService({ codeBlockStyle: 'fenced', headingStyle: 'atx' });
  return (html) => td.turndown(html || '');
})();

/**
 * RichTextEditor
 * @param {string} value - contenu initial (markdown ou html)
 * @param {'markdown'|'html'} [valueFormat] - forcer le format si connu
 * @param {(payload:{html:string, markdown:string, text:string})=>void} onChange
 * @param {string} placeholder
 * @param {('minimal'|'full')} [toolbar] - niveau de toolbar
 */
export default function RichTextEditor({
  value = '',
  valueFormat,
  onChange,
  placeholder = 'Saisir votre texte…',
  toolbar = 'full',
}) {
  // HTML initial (depuis markdown si besoin)
  const initialHTML = useMemo(() => {
    if (valueFormat === 'html') return value || '';
    if (valueFormat === 'markdown') return toHTML(value || '');
    // auto
    return toHTML(value || '');
  }, [value, valueFormat]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
        codeBlock: false,
      }),
      // Link.configure({ 
      //   openOnClick: false,     // on évite d’ouvrir pendant l’édition
      //   autolink: true,
      //   HTMLAttributes: {
      //     target: '_blank',
      //     rel: 'noopener noreferrer', // évite le reverse-tabnabbing
      //   },
      //  }),
      Image,
    ],
    content: initialHTML || '',
    editorProps: {
      attributes: { class: styles.editorArea, 'data-placeholder': placeholder },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const markdown = toMarkdown(html);
      const text = editor.getText();
      onChange?.({ html, markdown, text });
    },
  });

  // Si la prop "value" change de l’extérieur, réinjecter proprement
  useEffect(() => {
    if (!editor) return;
    // Évite d’écraser la frappe si on a déjà initialisé et que la valeur n’a pas objectivement changé
    // if (!didInitRef.current) {
    //   didInitRef.current = true;
    //   editor.commands.setContent(initialHTML, false);
    // } else {
    //   // MAJ contrôlée : uniquement si le html résultant diffère réellement
    //   const current = editor.getHTML();
    //   if (current.trim() !== initialHTML.trim()) {
    //     editor.commands.setContent(initialHTML, false);
    //   }
    // }
    if (editor.isFocused) return;
    const current = editor.getHTML();
    if (current.trim() !== initialHTML.trim()) {
      // false pour ne pas émettre un nouvel onUpdate
      editor.commands.setContent(initialHTML, false);
    }
  }, [editor, initialHTML]);

  if (!editor) return null;

  return (
    <div className={styles.wrapper}>
      <Toolbar editor={editor} mode={toolbar} />
      <div className={styles.surface}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function Toolbar({ editor, mode }) {
  if (!editor) return null;
  const Button = ({ onClick, active, title, children }) => (
    <button
      type="button"
      className={`${styles.btn} ${active ? styles.active : ''}`}
      onClick={onClick}
      title={title}
    >{children}</button>
  );
  return (
    <div className={styles.toolbar}>
      <Button active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Gras"><Bold size={16} /></Button>
      <Button active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italique"><Italic size={16} /></Button>
      <Button active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Barré"><Strikethrough size={16} /></Button>
      <span className={styles.sep} />
      <Button active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces"><List size={16} /></Button>
      <Button active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée"><ListOrdered size={16} /></Button>
      <span className={styles.sep} />
      <Button active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citation"><Quote size={16} /></Button>
      {/* <Button active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Code">{`</>`}</Button> */}
      <span className={styles.sep} />
      <Button onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Séparateur"><Minus size={16} /></Button>
      {mode === 'full' && (
        <>
          {/* <Button onClick={() => {
            const url = window.prompt('URL du lien :', 'https://');
            if (!url) return;
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
          }} active={editor.isActive('link')} title="Lien"><LinkIcon size={16} /></Button> */}
          <Button onClick={() => {
            const url = window.prompt('URL de l’image :', '');
            if (!url) return;
            editor.chain().focus().setImage({ src: url }).run();
          }} title="Image"><ImageIcon size={16} /></Button>
        </>
      )}
      <span className={styles.flex} />
      <button type="button" className={styles.btn} onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
        <Eraser size={16} />
      </button>
    </div>
  );
}
